/* eslint-disable import/prefer-default-export */
import { useCallback, useEffect, useMemo } from "react"
import { APPROVE_AND_CALL_TOKENS } from "@mezo/sdk"
import { PayloadAction } from "@reduxjs/toolkit"

import {
  approveToken,
  RootState,
  selectActiveToken,
  selectDepositAmount,
  selectTokenAllowance,
  selectTokenApprovalStatus,
  selectTokenDecimals,
  setHasApprovalError,
  setTokenApprovalStatus,
  useDappDispatch,
  useDappSelector,
} from "#/redux-state"
import { floatToBigInt } from "#/shared/utils"

export const useTokenApproval = (): {
  approveToken: () => Promise<void>
} => {
  const depositAmount = useDappSelector(selectDepositAmount)
  const activeToken = useDappSelector(selectActiveToken)
  const tokenApprovalStatus = useDappSelector(selectTokenApprovalStatus)

  const dispatch = useDappDispatch()

  const tokenDecimals = useDappSelector((state: RootState) =>
    selectTokenDecimals(state, activeToken),
  )

  const tokenAllowance = useDappSelector((state: RootState) =>
    selectTokenAllowance(state, activeToken),
  )

  const amount = useMemo(
    () => floatToBigInt(depositAmount, tokenDecimals),
    [depositAmount, tokenDecimals],
  )

  useEffect(() => {
    // If `approveAndCall` token automatically approve it
    if (APPROVE_AND_CALL_TOKENS.includes(activeToken)) {
      dispatch(setTokenApprovalStatus("approved"))
    } else {
      dispatch(
        setTokenApprovalStatus(
          tokenAllowance >= amount ? "approved" : "non-approved",
        ),
      )
    }
  }, [amount, tokenAllowance, activeToken, dispatch])

  const approveTokenHandler = useCallback(async () => {
    if (tokenApprovalStatus === "approved" || amount <= 0n) return
    dispatch(setTokenApprovalStatus("pending"))

    const { payload: approval } = (await dispatch(
      approveToken({ amount, token: activeToken }),
    )) as PayloadAction<boolean>

    dispatch(setHasApprovalError(!approval))
    dispatch(setTokenApprovalStatus(approval ? "approved" : "non-approved"))
  }, [amount, dispatch, activeToken, tokenApprovalStatus])

  return {
    approveToken: approveTokenHandler,
  }
}
